import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from "react";
import { formatCoordinationTime } from "@poscon/shared-types/eram";
import { removeStringFromEnd } from "@poscon/shared-types";
import { MenuSegment } from "./MenuSegment.js";
import { MovableMenu } from "./MovableMenu.js";
import { eramFontNameMap, eramTextDimensionMap, getBitmapTextStyles } from "../../eramFont.js";
import { MenuElement } from "./MenuElement.js";
import { colorNameMap } from "../../colorNameMap.js";
import { baseEramHubConnection } from "../../eramHubConnectionFactory.js";
const fontFamily = eramFontNameMap[2];
const { width: fontWidth, height: fontHeight } = eramTextDimensionMap[2];
const ROUTE_WRAP_WIDTH = 50;
const ROW_HEIGHT = fontHeight + 2;
const ROW_GAP = 4;
/** Wraps an FAA route string at `ROUTE_WRAP_WIDTH` characters without
 * splitting a fix/airway token, per TI 6110.101 §10.2.1.3.2.2/§10.2.1.3.2.3. */
function wrapRouteText(route, width = ROUTE_WRAP_WIDTH) {
    const tokens = route.split(/(\.+)/).filter(Boolean);
    const lines = [];
    let current = "";
    for (const token of tokens) {
        if (current.length > 0 && current.length + token.length > width) {
            lines.push(current);
            current = token.replace(/^\.+/, "");
        }
        else {
            current += token;
        }
    }
    if (current.length > 0) {
        lines.push(current);
    }
    return lines.length > 0 ? lines : [""];
}
export const PreviousRouteMenu = ({ fp, zIndex = 0, pos, startDrag, closeMenu, eventMode, ref }) => {
    const [error, setError] = useState(null);
    const previousRoute = fp.previousRoute
        ? removeStringFromEnd(fp.previousRoute.route.replace(/^\.*/, ""), fp.previousRoute.destination) +
            fp.previousRoute.destination
        : null;
    const routeLines = previousRoute ? wrapRouteText(previousRoute) : [];
    const tfmReroute = fp.tfmReroute;
    const width = 58 * fontWidth;
    const fixTimeY = 4;
    const rteY = fixTimeY + ROW_HEIGHT + 2 + ROW_GAP;
    const rteHeight = Math.max(routeLines.length, 1) * ROW_HEIGHT + 4;
    let y = rteY + rteHeight + ROW_GAP;
    const tfmY = tfmReroute ? y : null;
    if (tfmReroute) {
        y += ROW_HEIGHT + ROW_GAP;
    }
    const pendingRerouteY = tfmReroute ? y : null;
    if (tfmReroute) {
        y += ROW_HEIGHT + 8 + ROW_GAP;
    }
    const errorY = error ? y : null;
    if (error) {
        y += ROW_HEIGHT + ROW_GAP;
    }
    const applyY = y + 2;
    const height = applyY + ROW_HEIGHT + 12;
    const applyPreviousRoute = () => {
        baseEramHubConnection
            .applyPreviousRoute(fp.id)
            .then((response) => {
            if (response.isSuccess === false) {
                setError(response.lines.join(" ") || "REJECT");
                return;
            }
            closeMenu();
        })
            .catch(() => setError("REJECT - NO SYSTEM RESPONSE"));
    };
    return (_jsxs(MovableMenu, { ref: ref, menuName: "PREV_ROUTE_MENU", eventMode: eventMode, title: `PREVIOUS ROUTE ${fp.cid ?? ""} ${fp.callsign}`.replace(/\s+/g, " ").trim(), width: width, height: height, startDrag: startDrag, zIndex: zIndex, pos: pos, onClose: closeMenu, children: [_jsxs(MenuSegment, { width: width, fillColor: 0, y: fixTimeY, height: ROW_HEIGHT + 2, children: [_jsx("bitmapText", { x: 2, y: 2, text: "FIX", style: { ...getBitmapTextStyles(fontFamily), fill: colorNameMap.darkGrey } }), _jsx("bitmapText", { x: fontWidth * 4 + 2, y: 2, text: fp.coordinationFix?.fix ?? "---", style: { ...getBitmapTextStyles(fontFamily), fill: colorNameMap.lightGrey } }), _jsx("bitmapText", { x: fontWidth * 26, y: 2, text: "TIME", style: { ...getBitmapTextStyles(fontFamily), fill: colorNameMap.darkGrey } }), _jsx("bitmapText", { x: fontWidth * 31, y: 2, text: fp.coordinationFix ? formatCoordinationTime(fp.coordinationFix) : "----", style: { ...getBitmapTextStyles(fontFamily), fill: colorNameMap.lightGrey } })] }), _jsx(MenuSegment, { width: width, fillColor: 0, y: rteY, height: rteHeight, children: previousRoute ? (routeLines.map((line, i) => (_jsxs(React.Fragment, { children: [i === 0 && (_jsx("bitmapText", { x: 2, y: 2, text: "RTE", style: { ...getBitmapTextStyles(fontFamily), fill: colorNameMap.darkGrey } })), _jsx("bitmapText", { x: fontWidth * 4 + 2, y: 2 + ROW_HEIGHT * i, text: line, style: { ...getBitmapTextStyles(fontFamily), fill: colorNameMap.white } })] }, i)))) : (_jsx("bitmapText", { x: 2, y: 2, text: "NO PREVIOUS ROUTE DATA", style: { ...getBitmapTextStyles(fontFamily), fill: colorNameMap.lightGrey } })) }), tfmReroute && tfmY !== null && (_jsx("bitmapText", { x: 2, y: tfmY, text: `> ${tfmReroute.protectedRouteSegment} <`, style: { ...getBitmapTextStyles(fontFamily), fill: colorNameMap.yellow } })), tfmReroute && pendingRerouteY !== null && (_jsx(MenuElement, { x: Math.floor(width / 2 - (fontWidth * 24 + 4) / 2), y: pendingRerouteY, width: fontWidth * 24 + 4, text: "REAPPLY PENDING REROUTE", fillColor: 0, borderColor: colorNameMap.white, tint: colorNameMap.white, disabled: true })), error && errorY !== null && (_jsx("bitmapText", { x: 2, y: errorY, text: error, style: { ...getBitmapTextStyles(fontFamily), fill: colorNameMap.red } })), _jsx(MenuElement, { x: Math.floor(width / 2 - (fontWidth * 20 + 8) / 2), y: applyY, width: fontWidth * 20 + 8, text: "APPLY PREVIOUS ROUTE", fillColor: colorNameMap.cyan, borderColor: 0xadadad, disabled: !previousRoute, onmousedown: applyPreviousRoute })] }));
};
//# sourceMappingURL=PreviousRouteMenu.js.map